<template>
  <form
    class="verification-cdd-form"
    @submit.prevent="isFormValid() && showConfirmation()"
    novalidate
  >
    <div class="verification-cdd-form__section">
      <template
        v-if="userDetails.isNotEmptyData ||
          userDetails.isNotEmptyDocImages">
        <verification-form-documents
          :former="former"
          :user-details="userDetails"
          :is-disabled="formMixin.isDisabled" />
        <verification-details-anchors
          :verification-details="verificationDetails"
          :user-details="userDetails" />
      </template>
      <template v-else>
        <div class="verification-cdd-form__no-data-msg">
          <h2 class="verification-cdd-form__title--warning">
            {{ 'verification-cdd-form.empty-data-message'
              | globalize }}
          </h2>
        </div>
      </template>
    </div>
    <div class="verification-cdd-form__section">
      <div
        class="verification-cdd-form__address-details"
        v-for="item in userAddresses.ethereumAddresses"
        :key="`address-${item.address}-${item.chainId}`"
      >
        <ul class="app-key-value__list">
          <li class="app-key-value__row">
            <span class="app-key-value__key">
              {{ 'verification-cdd-form.lbl-chain-name' | globalize }}
            </span>
            <span
              class="app-key-value__value"
              :title="item.chainName"
            >
              {{ item.chainName }}
            </span>
          </li>

          <li class="app-key-value__row">
            <span class="app-key-value__key">
              {{ 'verification-cdd-form.lbl-chain-id' | globalize }}
            </span>
            <span
              class="app-key-value__value"
              :title="item.chainId"
            >
              {{ item.chainId }}
            </span>
          </li>

          <li class="app-key-value__row">
            <span class="app-key-value__key">
              {{ 'verification-cdd-form.lbl-address' | globalize }}
            </span>
            <span
              class="app-key-value__value"
              :title="item.address"
            >
              <copyable
                class="app-key-value__value"
                :copy-text="item.address">
                <a
                  target="_blank"
                  :href="explorerUrl('address', item.address, item.explorerUrl)"
                  :title="item.address">
                  {{ shortener(item.address) }}
                </a>
              </copyable>
            </span>
          </li>

          <li class="app-key-value__row">
            <span class="app-key-value__key">
              {{ 'verification-cdd-form.lbl-cpk-address' | globalize }}
            </span>
            <span
              class="app-key-value__value"
              :title="item.cpkAddress"
            >
              <copyable
                class="app-key-value__value"
                :copy-text="item.cpkAddress">
                <a
                  target="_blank"
                  :href="explorerUrl('address', item.cpkAddress, item.explorerUrl)"
                  :title="item.cpkAddress">
                  {{ shortener(item.cpkAddress) }}
                </a>
              </copyable>
            </span>
          </li>

          <li class="app-key-value__row">
            <span class="app-key-value__key">
              {{ 'verification-cdd-form.lbl-valega-results' | globalize }}
            </span>
            <span
              class="app-key-value__value"
              :title="item.riskLevel"
            >
              <span class="risk-level">
                {{ 'verification-cdd-form.lbl-risk-level' | globalize }}: {{ item.riskLevel }}
              </span>

              <permission-wrapper
                skip-alert
                page-name="verifications"
                resource-name="limited_risk_report"
                action-name="read_foreign">
                <limited-risk-report
                  :load-data="() => loadRiskReport(item.address)"
                  :loading="riskReportLoading"
                />
              </permission-wrapper>
              <span>
                <a-popconfirm
                  :title="'verification-cdd-form.quest-logged-in-valegachain' | globalize"
                  :ok-text="'roles.answer-yes' | globalize"
                  :cancel-text="'roles.answer-no' | globalize"
                  @confirm="() => openURL(item.reportURL)">
                  <a-button
                    type="default"
                  >
                    Open
                  </a-button>
                  <span class="help">
                    <a-tooltip
                      placement="top"
                      :title="'verification-cdd-form.help-direct-risk-report' | globalize"
                    >
                      <a-icon type="question-circle" />
                    </a-tooltip>
                  </span>
                </a-popconfirm>
              </span>
            </span>
          </li>

          <li class="app-key-value__row">
            <span class="app-key-value__key">
              {{ 'verification-cdd-form.lbl-chainalisys-results' | globalize }}
            </span>
            <span
              class="app-key-value__value"
              :title="item.riskLevel"
            >
              <chainalysis-risk-report :data-source="item.chainalysis" />
            </span>
          </li>
        </ul>
        <a-divider />
      </div>
    </div>

    <div class="verification-cdd-form__section">
      <verification-form-common-risk
        :former="former"
        :user-details="userDetails"
        :is-disabled="formMixin.isDisabled" />
    </div>

    <div class="verification-cdd-form__section">
      <verification-form-product-risk
        :former="former"
        :is-disabled="formMixin.isDisabled" />
    </div>

    <div class="verification-cdd-form__section">
      <verification-form-geo-risk
        :former="former"
        :is-disabled="formMixin.isDisabled" />
    </div>

    <div class="verification-cdd-form__section">
      <verification-form-pep
        :former="former"
        :user-details="userDetails"
        :verification-details="verificationDetails"
        :is-disabled="formMixin.isDisabled" />
    </div>

    <div class="verification-cdd-form__section">
      <verification-form-check-list
        :former="former"
        :user-details="userDetails"
        :is-disabled="formMixin.isDisabled" />
    </div>

    <div class="verification-cdd-form__section">
      <verification-form-risk-assmnt
        :former="former"
        :is-disabled="formMixin.isDisabled" />
    </div>

    <p
      v-if="!formValidationMixin.isFormValid"
      class="verification-cdd-form__error-msg">
      {{ 'verification-cdd-form.validation-message' | globalize }}
    </p>

    <div
      v-if="!isVerificationComplete"
      class="app__form-actions verification-cdd-form__actions">
      <form-confirmation
        v-if="formMixin.isConfirmationShown"
        :is-pending="isFormSubmitting"
        @ok="chooseMethod"
        @cancel="hideConfirmation"
      />
      <template v-else>
        <div class="verification-cdd-form__buttons-first-block">
          <permission-wrapper
            skip-alert
            page-name="verifications"
            resource-name="verification"
            action-name="update_foreign">
            <button
              data-html2canvas-ignore
              v-ripple
              class="app__form-submit-btn app__button-raised"
              @click="verificationStatus = REQUEST_STATUSES.approved"
              :disabled="formMixin.isDisabled || !userDetails.isNotEmptyData"
            >
              {{ 'verification-cdd-form.btn-approve' | globalize }}
            </button>
            <button
              data-html2canvas-ignore
              v-ripple
              class="app__form-submit-btn app__button-raised--danger"
              @click="verificationStatus = REQUEST_STATUSES.rejected;
                      showConfirmation()"
              :disabled="formMixin.isDisabled"
            >
              {{ 'verification-cdd-form.btn-reject' | globalize }}
            </button>
          </permission-wrapper>
        </div>
        <div class="verification-cdd-form__buttons-second-block">
          <permission-wrapper
            skip-alert
            page-name="verifications"
            resource-name="verification"
            action-name="update_foreign">
            <!-- eslint-disable -->
            <button
              data-html2canvas-ignore
              v-ripple
              class="app__form-submit-btn app__button-raised--primary"
              @click.prevent="verificationStatus = REQUEST_STATUSES.in_progress;
                              showConfirmation()"
              :disabled="formMixin.isDisabled && isFormSubmitting"
            >
              {{ 'verification-cdd-form.btn-continue' | globalize }}
            </button>
            <button
              data-html2canvas-ignore
              class="app__form-submit-btn
                app__button-raised
                app__button-raised--attention"
              @click="verificationStatus = REQUEST_STATUSES.discard;
                      showConfirmation()"
              :disabled="formMixin.isDisabled"
            >
              {{ 'verification-cdd-form.btn-discard' | globalize }}
            </button>
          </permission-wrapper>
        </div>
      </template>
    </div>
    <textarea-field
      class="verification-cdd-form__field"
      v-if="verificationStatus === REQUEST_STATUSES.rejected && reason"
      v-model="reason"
      @input="former.setAttr('rejectReason.reason', reason)"
      :label="'verification-cdd-form.lbl-reason' | globalize"
      :disabled="isFormSubmitting || Boolean(verificationDetails.reason.reason)"
    />
  </form>
</template>

<script>
import VerificationFormDocuments from './components/VerificationFormDocuments'
import VerificationFormCommonRisk from './components/VerificationFormCommonRisk'
import VerificationFormProductRisk from './components/VerificationFormProductRisk'
import VerificationFormGeoRisk from './components/VerificationFormGeoRisk'
import VerificationFormPEP from './components/VerificationFormPEP'
import VerificationFormCheckList from './components/VerificationFormCheckList'
import VerificationFormRiskAssmnt from './components/VerificationFormRiskAssmnt'
import VerificationDetailsAnchors from '@/vue/pages/verifications/components/VerificationDetailsAnchors'
import LimitedRiskReport from '@/vue/common/popups/limited-risk-report'
import PermissionWrapper from '@/vue/common/permission-wrapper'
import Copyable from '@/vue/common/copyable/Copyable'
import { VerificationRecord } from '@/js/records/entities/verification.record'
import { VerificationCDDFormer } from '@/js/formers/VerificationCDDFormer'
import { UserRecord } from '@/js/records/entities/user.record'
import FormMixin from '@/vue/mixins/form.mixin'
import { ErrorHandler } from '@/js/helpers/error-handler'
import { api } from '@/api'
import { vuexTypes } from '@/vuex'
import { vueRoutes } from '@/vue-router/routes'
import { mapGetters } from 'vuex'
import { Bus } from '@/js/helpers/event-bus'
import { shortener } from '@/js/helpers/string'
import { explorerUrl } from '@/js/helpers/urls'
import { REQUEST_STATUSES, REQUEST_TYPES } from '@/js/enums'
import AButton from 'ant-design-vue/lib/button'
import AIcon from 'ant-design-vue/lib/icon'
import ATooltip from 'ant-design-vue/lib/tooltip'
import APopConfirm from 'ant-design-vue/lib/popconfirm'
import ADivider from 'ant-design-vue/lib/divider'
import ChainalysisRiskReport from '@/vue/common/chainalysis'

export default {
  name: 'verification-cdd-form',
  components: {
    VerificationFormDocuments,
    VerificationFormProductRisk,
    VerificationFormGeoRisk,
    VerificationFormCommonRisk,
    'verification-form-pep': VerificationFormPEP,
    VerificationFormCheckList,
    VerificationFormRiskAssmnt,
    VerificationDetailsAnchors,
    Copyable,
    PermissionWrapper,
    AButton,
    AIcon,
    ATooltip,
    LimitedRiskReport,
    'a-popconfirm': APopConfirm,
    ADivider,
    ChainalysisRiskReport,
  },
  mixins: [ FormMixin ],
  props: {
    verificationDetails: {
      type: VerificationRecord,
      required: true,
    },
    userDetails: {
      type: UserRecord,
      required: true,
    },
    userAddresses: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      REQUEST_STATUSES,
      riskReportLoading: false,
      isFormSubmitting: false,
      former: '',
      verificationStatus: this.verificationDetails.status || '',
      reason: this.verificationDetails.reason.reason || '',
    }
  },

  computed: {
    ...mapGetters([
      vuexTypes.accountAddress,
      vuexTypes.accountId,
    ]),

    isVerificationComplete () {
      const status = this.verificationDetails.status
      return status !== REQUEST_STATUSES.pending_manual &&
        status !== REQUEST_STATUSES.in_progress
    },
  },

  created () {
    this.former = new VerificationCDDFormer(this.verificationDetails.manual)

    if (this.isVerificationComplete) {
      this.disableForm()
    }
  },

  methods: {
    async submit () {
      this.isFormSubmitting = true

      if (this.former.attrs.newPlaceOfBirth) {
        await this.sendPlaceOfBirth()
      }

      let formBuild = this.former.buildOps()

      let extraData = {}
      if (this.verificationStatus === REQUEST_STATUSES.pending_manual ||
          this.verificationStatus === REQUEST_STATUSES.in_progress) {
        formBuild.riskAssmnt.subscriber = ''
        formBuild.riskAssmnt.signatureDate = ''
        this.verificationStatus = REQUEST_STATUSES.in_progress
      } else if (this.verificationStatus === REQUEST_STATUSES.approved ||
        this.verificationStatus === REQUEST_STATUSES.rejected) {
        extraData = {
          documentImages: this.userDetails.documents.documentImages,
          selfie: this.userDetails.documents.selfie,
          documentDetails: this.userDetails.documentsInfo.documentDetails,
        }
      }

      try {
        const body = JSON.stringify({
          data: {
            type: REQUEST_TYPES.update,
            attributes: {
              status: this.verificationStatus,
              data: {
                manual: {
                  ...formBuild,
                  logs: [...this.verificationDetails.logs, {
                    admin_address: this.accountAddress,
                    admin_user_id: this.accountId || 0,
                    action: this.verificationStatus,
                    date: new Date().toISOString(),
                  }],
                  imagesData: extraData,
                },
              },
              reason: formBuild.rejectReason,
            },
          },
        })

        await api.patchWithSignature(`/verifications/${this.verificationDetails.id}`, body)
        const translation = this.getTranslationFeedback()
        Bus.success(translation)
        this.updateRouter()
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }

      this.hideConfirmation()
      this.isFormSubmitting = false
    },

    async sendPlaceOfBirth () {
      let body = JSON.stringify({
        data: {
          id: String(this.userDetails.id),
          type: 'patch_userdata',
          attributes: {
            place_of_birth: this.former.attrs.newPlaceOfBirth,
          },
        },
      })

      try {
        await api.patchWithSignature(`/userdata/${this.userDetails.id}`, body)
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
    },

    getTranslationFeedback () {
      let translation
      switch (this.verificationStatus) {
        case REQUEST_STATUSES.approved:
          translation = 'verification-cdd-form.success-approve-message'
          break
        case REQUEST_STATUSES.rejected:
          translation = 'verification-cdd-form.success-reject-message'
          break
        case REQUEST_STATUSES.pending_manual:
          translation = 'verification-cdd-form.success-save-message'
          break
        case REQUEST_STATUSES.in_progress:
          translation = 'verification-cdd-form.success-save-message'
          break
        case REQUEST_STATUSES.discard:
          translation = 'verification-cdd-form.success-discard-message'
          break
      }
      return translation
    },

    updateRouter () {
      if (this.verificationStatus === REQUEST_STATUSES.pending_manual ||
          this.verificationStatus === REQUEST_STATUSES.in_progress ||
          this.verificationStatus === REQUEST_STATUSES.discard) {
        this.redirectToVerificationsList()
      } else {
        this.redirectToUser()
      }
    },

    redirectToVerificationsList () {
      this.$router.push({
        name: vueRoutes.verificationsList.name,
        params: { id: this.verificationDetails.userId },
      })
    },

    redirectToUser () {
      this.$router.push({
        name: vueRoutes.userDetails.name,
        params: { id: this.verificationDetails.userId },
      })
    },

    chooseMethod () {
      if (this.verificationStatus === REQUEST_STATUSES.discard) {
        this.deleteVerification()
      } else {
        this.submit()
      }
    },

    async deleteVerification () {
      this.isFormSubmitting = true

      try {
        const endpoint = `/verifications/${this.verificationDetails.id}`
        await api.deleteWithSignature(endpoint)
        const translation = this.getTranslationFeedback()
        Bus.success(translation)
        this.updateRouter()
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }

      this.hideConfirmation()
      this.isFormSubmitting = false
    },

    async loadRiskReport (address) {
      this.riskReportLoading = true
      try {
        const response = await api.getWithSignature(`/addresses/${address}/limited-risk-report`)
        return response
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      } finally {
        this.riskReportLoading = false
      }
    },

    openURL (url) {
      if (!url) {
        return
      }
      window.open(url, '_blank')
    },

    shortener,
    explorerUrl,
  },
}
</script>

<style lang="scss">
@import './verification-cdd-form';
@import '~ant-design-vue/lib/button/style/index.css';
@import '~ant-design-vue/lib/icon/style/index.css';
@import '~ant-design-vue/lib/tooltip/style/index.css';
@import '~ant-design-vue/lib/popover/style/index.css';
@import '~ant-design-vue/lib/divider/style/index.css';

.risk-level {
  margin-right: 1em;
}
</style>
